import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import { useSEO } from '../../utils/seo';

export default function MovingPage() {
  const navigate = useNavigate();

  const handleContactForm = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/#contact-form');
    setTimeout(() => {
      const el = document.getElementById('contact-form');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useSEO({
    title: '長崎引越しサービス | みなとサービス - 単身・家族・オフィス移転対応',
    description: '長崎県の引越しサービス。単身からご家族、オフィス移転まで対応。経験豊富なスタッフが大切な荷物を丁寧に運びます。即日対応可能、お見積もり無料。貨物保険完備で安心。24時間受付中。',
    keywords: '長崎,引越し,単身引越し,家族引越し,オフィス移転,引越し業者,長崎県,即日対応,見積もり無料,貨物保険',
    canonical: '/moving',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '引越しサービス',
      description: '長崎県の引越しサービス。単身からご家族、オフィス移転まで対応。経験豊富なスタッフが大切な荷物を丁寧に運びます。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'みなとサービス',
        telephone: '0120-743-643',
        address: {
          '@type': 'PostalAddress',
          addressRegion: '長崎県',
          addressCountry: 'JP'
        }
      },
      areaServed: {
        '@type': 'Place',
        name: '長崎県'
      },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: '14000',
          priceCurrency: 'JPY',
          description: '基本料金（軽トラック1台、2時間まで）'
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/ac4a641a777f50f3be7ba0e81f9de0cb.jpg"
            alt="引越しサービス"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6">
            引越しサービス
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl mb-3 md:mb-4 text-white/90 leading-relaxed">
            単身からご家族、オフィス移転まで、<br className="sm:hidden" />
            あらゆる引越しに対応。<br className="hidden sm:block" />
            大切な荷物を丁寧に運びます
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 mb-5 md:mb-8 inline-block border border-white/30">
            <p className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-white flex items-center gap-2">
              <i className="ri-map-pin-fill text-[#ebe960] text-lg md:text-xl"></i>
              長崎県内・全国各地への<br className="sm:hidden" />
              引越しに対応
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link
              to="/contact"
              className="bg-[#ebe960] text-gray-900 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-[#d4d34d] transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-mail-line text-lg md:text-xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-gradient-to-br from-[#ebe960] to-[#d4d24e] rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                単身者の方へ
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-800 mb-6 md:mb-8 leading-relaxed">
                初めての一人暮らし、転勤、進学など、<br className="hidden sm:block" />
                単身での引越しは何かと不安がつきもの。<br />
                私たちは、荷物が少ない単身者の方でも<br className="hidden sm:block" />
                気軽にご利用いただける<br className="sm:hidden" />
                リーズナブルな料金と<br className="hidden sm:block" />
                丁寧なサービスをご提供しています。
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mt-6 md:mt-10">
                <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className="ri-wallet-3-fill text-xl md:text-3xl text-[#ebe960]"></i>
                  </div>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                    お手頃な価格
                  </h3>
                  <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                    軽トラック1台で<br />¥14,000〜とリーズナブル
                  </p>
                </div>

                <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className="ri-timer-flash-fill text-xl md:text-3xl text-[#ebe960]"></i>
                  </div>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                    スピーディー
                  </h3>
                  <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                    荷物が少なければ<br />2時間程度で完了
                  </p>
                </div>

                <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className="ri-box-3-fill text-xl md:text-3xl text-[#ebe960]"></i>
                  </div>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                    梱包サポート
                  </h3>
                  <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                    梱包が苦手な方も<br />お手伝いします
                  </p>
                </div>
              </div>

              <div className="mt-6 md:mt-8 bg-white/50 rounded-xl md:rounded-2xl p-4 md:p-6">
                <p className="text-gray-800 text-xs md:text-sm leading-relaxed">
                  <strong>こんな方におすすめ：</strong><br />
                  初めての一人暮らし / 学生・新社会人の方 / <br className="sm:hidden" />
                  転勤が多い方 / 荷物が少ない方 / 費用を抑えたい方
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              料金体系
            </h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600">
              明朗会計で安心してご利用いただけます
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-[#ede9e3] rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 lg:p-12 mb-6 md:mb-8">
              <div className="text-center mb-6 md:mb-8">
                <div className="inline-block bg-[#ebe960] rounded-full px-4 md:px-6 py-2 md:py-3 mb-3 md:mb-4">
                  <p className="text-sm md:text-base lg:text-lg font-bold text-gray-900">基本料金</p>
                </div>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                  ¥14,000〜
                </div>
                <p className="text-gray-700 text-sm md:text-base lg:text-lg">
                  軽トラック１台、2時間までの作業
                </p>
              </div>

              <div className="border-t-2 border-gray-300 pt-6 md:pt-8">
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                  料金に含まれるもの
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                  {[
                    '車両費用（軽トラック）',
                    '基本的な養生作業',
                    '簡易的な梱包サポート',
                    '家具や家電の設置・組立'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <i className="ri-check-line text-[#ebe960] text-lg md:text-xl flex-shrink-0"></i>
                      <span className="text-gray-700 text-xs sm:text-sm md:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 md:p-6 bg-white rounded-xl md:rounded-2xl border-2 border-gray-200">
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose">
                <strong>※料金について</strong>
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                上記は基本料金です。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                階段作業、エレベーターなし、<br className="hidden sm:block" />
                狭い場所 (車両が入らない) などの条件により<br className="sm:hidden" />
                追加料金が発生する場合があります。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-2">
                また、エリアや時期(3月・4月)によって、<br className="sm:hidden" />
                料金が変動することがございます。
              </p>
              <p className="text-gray-700 text-xs md:text-sm lg:text-base leading-loose mt-4">
                詳しくはお見積もり時にご説明いたします。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              引越しの流れ
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              {
                step: '01',
                title: 'お問い合わせ',
                description: 'お電話またはメールでお気軽にご相談ください',
                icon: 'ri-phone-fill'
              },
              {
                step: '02',
                title: 'お見積もり',
                description: '荷物の量を確認し、無料でお見積もりいたします',
                icon: 'ri-file-list-3-fill'
              },
              {
                step: '03',
                title: '作業日決定',
                description: 'ご都合に合わせて作業日を調整いたします',
                icon: 'ri-calendar-check-fill'
              },
              {
                step: '04',
                title: '引越し作業',
                description: '丁寧かつ迅速に作業を行います',
                icon: 'ri-truck-fill'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <i className={`${item.icon} text-2xl sm:text-3xl md:text-4xl text-gray-900`}></i>
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-200 mb-1 md:mb-2">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              安心のポイント
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8">
            <div className="bg-[#ede9e3] rounded-xl md:rounded-3xl p-4 md:p-8 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                <i className="ri-shield-check-fill text-xl sm:text-2xl md:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                貨物保険完備
              </h3>
              <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                万が一の事故に備え、貨物保険に加入しています。
              </p>
            </div>

            <div className="bg-[#ede9e3] rounded-xl md:rounded-3xl p-4 md:p-8 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                <i className="ri-team-fill text-xl sm:text-2xl md:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                経験豊富なスタッフ
              </h3>
              <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                引越しのプロが丁寧に作業を行います。
              </p>
            </div>

            <div className="bg-[#ede9e3] rounded-xl md:rounded-3xl p-4 md:p-8 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                <i className="ri-box-3-fill text-xl sm:text-2xl md:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                丁寧な梱包
              </h3>
              <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                大切な荷物を傷つけないよう、しっかり梱包します。
              </p>
            </div>

            <div className="bg-[#ede9e3] rounded-xl md:rounded-3xl p-4 md:p-8 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-[#ebe960] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                <i className="ri-calendar-check-fill text-xl sm:text-2xl md:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                柔軟な日程調整
              </h3>
              <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed">
                土日祝日も対応可能。お客様のご都合に合わせます。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-[#ede9e3]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
            お見積もり・ご相談無料
          </h2>
          <p className="text-sm md:text-lg lg:text-xl text-gray-700 mb-6 md:mb-8">
            まずはお気軽にご相談ください。
          </p>
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 mb-8 md:mb-12 inline-block">
            <p className="text-sm md:text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2 md:gap-3">
              <i className="ri-map-2-fill text-[#ebe960] text-xl md:text-2xl"></i>
              長崎県内・全国各地への引越しに対応
            </p>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 mt-2">
              遠方への引越しもお任せください。<br className="sm:hidden" />
              詳しくはお問い合わせください。
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 md:gap-3 bg-gray-900 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-bold hover:bg-gray-800 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-fill text-lg sm:text-xl md:text-2xl"></i>
              お問い合わせはこちら
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LineFloatingButton />
    </div>
  );
}
